// A game's release panel on its dashboard page: coming soon or out, the expected date (editable), how many people
// wait, and "Release now", which notifies them. Accounts (wave 6): GET|PATCH /v1/creators/me/games/:id/waitlist and
// …/release, POST …/release. Rules and wording: src/release.ts.

import { api, ApiError, isMissingEndpoint } from "../api.ts";
import { expectedDate, refreshCatalogue, type Game } from "../catalogue.ts";
import { clear, errorBox, h, section, toast } from "../dom.ts";
import { viewerZone } from "../events.ts";
import {
  localToUnix,
  parseRelease,
  parseReleased,
  releaseActions,
  releaseConfirmText,
  releaseError,
  unixToLocal,
  type ReleaseInfo,
} from "../release.ts";

const base = (g: Game) => `/creators/me/games/${encodeURIComponent(g.id)}`;

function fail(out: HTMLElement, e: unknown): void {
  const words = e instanceof ApiError ? releaseError(e.code) : null;
  clear(out, words ? h("p", { class: "error", role: "alert" }, words) : errorBox(e));
}

export function releasePanel(g: Game): HTMLElement {
  const host = h("div");
  /** the outcome of Release now, kept on screen after the panel reloads */
  let outcome: string | null = null;
  const load = (said: string | null = outcome) => {
    outcome = said;
    clearHost();
  };
  const clearHost = () =>
    clear(
      host,
      section(
        "Release",
        async (body) => {
          let r: ReleaseInfo;
          try {
            r = parseRelease(await api("accounts", `${base(g)}/waitlist`), g.id);
          } catch (e) {
            if (isMissingEndpoint(e)) return clear(body, h("p", { class: "dim" }, "Release settings aren't available yet."));
            throw e;
          }
          draw(body, g, r, load, outcome);
        },
        { class: "section panel release-panel" },
      ),
    );
  load();
  return host;
}

function draw(body: HTMLElement, g: Game, r: ReleaseInfo, reload: (outcome: string | null) => void, outcome: string | null): void {
  const can = releaseActions(r);
  const msg = h("div", { "aria-live": "polite" });
  const zone = viewerZone().replace(/_/g, " ");
  const soon = r.status === "coming_soon";
  const expected = expectedDate(r.releaseAt, { withTime: true });

  const patch = async (payload: Record<string, unknown>, done: string) => {
    try {
      r = parseRelease(await api("accounts", `${base(g)}/release`, { method: "PATCH", body: payload }), g.id);
      refreshCatalogue();
      toast(done);
      draw(body, g, r, reload, outcome);
    } catch (e) {
      fail(msg, e);
    }
  };

  // the expected date, in the viewer's zone
  const when = h("input", { id: "rel-when", type: "datetime-local", value: r.releaseAt ? unixToLocal(r.releaseAt) : "" }) as HTMLInputElement;
  const dateForm = can.canEditDate
    ? h(
        "form",
        {
          class: "actions",
          onsubmit: (e: Event) => {
            e.preventDefault();
            const at = localToUnix(when.value);
            if (at === null) return clear(msg, h("p", { class: "error", role: "alert" }, "Pick a date and time."));
            if (at * 1000 <= Date.now()) return clear(msg, h("p", { class: "error", role: "alert" }, "Pick a time in the future."));
            void patch({ releaseAt: at }, "Expected date saved");
          },
        },
        h("label", { for: "rel-when" }, `Expected (${zone})`),
        when,
        h("button", { type: "submit", class: "btn small" }, "Save date"),
        r.releaseAt !== null ? h("button", { type: "button", class: "btn small ghost", onclick: () => void patch({ releaseAt: null }, "Date cleared: it shows as Coming soon") }, "Clear date") : null,
      )
    : null;

  // "Release now": a switch, confirmed first
  const sw = h("input", { type: "checkbox", role: "switch", id: "rel-now", "aria-describedby": "rel-now-help" }) as HTMLInputElement;
  sw.addEventListener("change", async () => {
    if (!sw.checked) return;
    if (!confirm(releaseConfirmText(g.title, r.waiting))) {
      sw.checked = false;
      return;
    }
    sw.disabled = true;
    try {
      const out = parseReleased(await api("accounts", `${base(g)}/release`, { method: "POST" }), g.id);
      refreshCatalogue();
      const said = out.alreadyReleased ? `${g.title} was already out; nobody new was notified.` : `${g.title} is out. ${out.notified} ${out.notified === 1 ? "player was" : "players were"} notified.`;
      toast(said);
      reload(said);
    } catch (e) {
      sw.checked = false;
      sw.disabled = false;
      fail(msg, e);
    }
  });

  clear(
    body,
    h(
      "div",
      { class: "kpis" },
      h("div", { class: "kpi" }, h("div", { class: "k" }, "Status"), h("div", { class: "v" }, h("span", { class: `badge ${soon ? "warn" : "ok"}` }, soon ? "Coming soon" : "Released"))),
      soon
        ? h("div", { class: "kpi" }, h("div", { class: "k" }, "Expected"), h("div", { class: "v small-v" }, expected ?? "No date shown"), r.releaseAt && !expected ? h("div", { class: "dim small" }, "The date has passed, so players see just Coming soon.") : null)
        : h("div", { class: "kpi" }, h("div", { class: "k" }, "Released"), h("div", { class: "v small-v" }, r.releasedAt ? (expectedDate(r.releasedAt, { withTime: true, now: 0 }) ?? "—") : "—")),
      h("div", { class: "kpi" }, h("div", { class: "k" }, "Waitlist"), h("div", { class: "v" }, String(r.waiting)), h("div", { class: "dim small" }, soon ? "People who asked to be told when it's out" : "People who were waiting")),
    ),
    soon
      ? [
          h("p", { class: "dim small" }, "While it's coming soon, the website and the Banger app show Notify me instead of Play. Players join the waitlist in Banger."),
          dateForm,
          h(
            "div",
            { class: "switch-row" },
            sw,
            h("label", { for: "rel-now" }, "Release now"),
            h("p", { id: "rel-now-help", class: "dim small" }, `Makes it playable and tells the ${r.waiting} ${r.waiting === 1 ? "person" : "people"} waiting, plus players who hearted or followed it. You'll be asked to confirm.`),
          ),
        ]
      : null,
    outcome ? h("p", { class: "note", role: "status" }, outcome) : null,
    msg,
  );
}
